import type {
  CalendarLanguages,
  CalendarYearType,
  StudyYearCalendar,
  StudyYearDayStatus,
} from '../../Types/interfaces';

export type DayStatus = StudyYearDayStatus;

// Dates as 'YYYY-MM-DD'
export type TeachingPeriod = { start: string; end: string };

// The days of a calendar as they are shown: its teaching period and the days marked in it,
// dates as 'YYYY-MM-DD'
export type CalendarDays = {
  startDate: string;
  endDate: string;
  freeDays: Array<string>;
  examDays: Array<string>;
  retakeDays: Array<string>;
  outsideDays: Array<string>;
  holidays: Array<string>;
};

// Each of the four calendars has its own two semesters
export type Semester = 'first' | 'second';

export const SEPTEMBER = 8;
export const OCTOBER = 9;
const FEBRUARY = 1;
const JULY = 6;
const AUGUST = 7;
const LAST_DAY_OF_AUGUST = 31;
const TEACHING_PERIOD_END_DAY = 5;
const SECOND_SEMESTER_START_DAY = 22;

// Local date as 'YYYY-MM-DD', so comparisons don't depend on the time zone
export const toDateKey = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

// Default teaching period of a study year: 1 October → 5 July of the next year
export const getDefaultTeachingPeriod = (
  studyYearStart: number
): TeachingPeriod => ({
  start: toDateKey(new Date(studyYearStart, OCTOBER, 1)),
  end: toDateKey(new Date(studyYearStart + 1, JULY, TEACHING_PERIOD_END_DAY)),
});

// Default period of a semester: 1 October → 21 February for the first one,
// 22 February → 5 July of the next year for the second one
export const getDefaultSemesterPeriod = (
  studyYearStart: number,
  semester: Semester
): TeachingPeriod =>
  semester === 'first'
    ? {
        start: toDateKey(new Date(studyYearStart, OCTOBER, 1)),
        end: toDateKey(
          new Date(studyYearStart + 1, FEBRUARY, SECOND_SEMESTER_START_DAY - 1)
        ),
      }
    : {
        start: toDateKey(
          new Date(studyYearStart + 1, FEBRUARY, SECOND_SEMESTER_START_DAY)
        ),
        end: toDateKey(
          new Date(studyYearStart + 1, JULY, TEACHING_PERIOD_END_DAY)
        ),
      };

const isWeekend = (date: Date): boolean =>
  date.getDay() === 0 || date.getDay() === 6;

export const getDefaultDayStatus = (
  date: Date,
  period: TeachingPeriod,
  holidayDateKeys: ReadonlySet<string>
): DayStatus => {
  const key = toDateKey(date);
  if (key < period.start || key > period.end) {
    return 'outside';
  }
  return isWeekend(date) || holidayDateKeys.has(key) ? 'free' : 'working';
};

// 'YYYY-MM-DD' as a local date, the opposite of toDateKey
export const fromDateKey = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Builds the days of a calendar from the teaching period, the days the rector changed
// and the dates of the public holidays, all keyed by 'YYYY-MM-DD'
export const buildAcademicCalendar = (
  studyYearStart: number,
  period: TeachingPeriod,
  overrides: Readonly<Record<string, DayStatus>>,
  holidayDateKeys: ReadonlySet<string>
): CalendarDays => {
  // Holidays are collected from 1 September → 31 August,
  // extended if the teaching period starts earlier or ends later
  const yearStart = toDateKey(new Date(studyYearStart, SEPTEMBER, 1));
  const yearEnd = toDateKey(
    new Date(studyYearStart + 1, AUGUST, LAST_DAY_OF_AUGUST)
  );
  const firstDay = period.start < yearStart ? period.start : yearStart;
  const lastDay = period.end > yearEnd ? period.end : yearEnd;

  const freeDays: Array<string> = [];
  const examDays: Array<string> = [];
  const retakeDays: Array<string> = [];
  const outsideDays: Array<string> = [];
  const holidays: Array<string> = [];

  const end = fromDateKey(lastDay);
  for (
    const date = fromDateKey(firstDay);
    date <= end;
    date.setDate(date.getDate() + 1)
  ) {
    const key = toDateKey(date);
    const defaultStatus = getDefaultDayStatus(date, period, holidayDateKeys);
    // Days outside the teaching period can't be changed
    if (defaultStatus !== 'outside') {
      const status = overrides[key] ?? defaultStatus;
      if (status === 'free') {
        freeDays.push(key);
      } else if (status === 'exam') {
        examDays.push(key);
      } else if (status === 'retake') {
        retakeDays.push(key);
      } else if (status === 'outside') {
        outsideDays.push(key);
      }
    }

    if (holidayDateKeys.has(key)) {
      holidays.push(key);
    }
  }

  return {
    startDate: period.start,
    endDate: period.end,
    freeDays,
    examDays,
    retakeDays,
    outsideDays,
    holidays,
  };
};

// A study year lasts until the day before the next study year starts, or for one year
// when the next one isn't created yet, like on the backend. Its teaching period ends earlier,
// with the second semester; the rest, e.g. the summer holiday, is outside of the teaching period
export const getStudyYearLastDay = (
  startKey: string,
  nextStartKey?: string
): string => {
  const nextStart = fromDateKey(nextStartKey ?? startKey);
  if (!nextStartKey) {
    nextStart.setFullYear(nextStart.getFullYear() + 1);
  }
  nextStart.setDate(nextStart.getDate() - 1);
  return toDateKey(nextStart);
};

// The dates ('YYYY-MM-DD') after the first one and before the second one
const getDaysBetween = (afterKey: string, beforeKey: string): Array<string> => {
  const days: Array<string> = [];
  const before = fromDateKey(beforeKey);
  const day = fromDateKey(afterKey);
  day.setDate(day.getDate() + 1);
  while (day < before) {
    days.push(toDateKey(day));
    day.setDate(day.getDate() + 1);
  }
  return days;
};

const mergeDays = (...lists: Array<Array<string>>): Array<string> =>
  [...new Set(lists.flat())].sort();

// The days of a calendar from the days of its two semesters, as they are shown.
// The days between the semesters are free days; only after the second semester is the summer holiday
export const mergeSemesters = (
  first: CalendarDays,
  second: CalendarDays
): CalendarDays => ({
  startDate: first.startDate,
  endDate: second.endDate,
  freeDays: mergeDays(
    first.freeDays,
    getDaysBetween(first.endDate, second.startDate),
    second.freeDays
  ),
  examDays: mergeDays(first.examDays, second.examDays),
  retakeDays: mergeDays(first.retakeDays, second.retakeDays),
  outsideDays: mergeDays(first.outsideDays, second.outsideDays),
  holidays: mergeDays(first.holidays, second.holidays),
});

// The calendar saved for a teaching language group and kind of years, from the days of its semesters.
// The days outside of the teaching period aren't listed, they come from the dates of the semesters
export const toStudyYearCalendar = (
  languages: CalendarLanguages,
  yearType: CalendarYearType,
  first: CalendarDays,
  second: CalendarDays
): StudyYearCalendar => ({
  languages,
  yearType,
  firstSemesterStart: first.startDate,
  firstSemesterEnd: first.endDate,
  secondSemesterStart: second.startDate,
  secondSemesterEnd: second.endDate,
  freeDays: mergeDays(first.freeDays, second.freeDays),
  examDays: mergeDays(first.examDays, second.examDays),
  retakeDays: mergeDays(first.retakeDays, second.retakeDays),
  holidays: mergeDays(first.holidays, second.holidays),
});

// The days of a saved calendar as they are shown, the opposite of toStudyYearCalendar.
// The days between the semesters aren't saved, they are shown as free days
export const toCalendarDays = (calendar: StudyYearCalendar): CalendarDays => ({
  startDate: calendar.firstSemesterStart,
  endDate: calendar.secondSemesterEnd,
  freeDays: mergeDays(
    calendar.freeDays,
    getDaysBetween(calendar.firstSemesterEnd, calendar.secondSemesterStart)
  ),
  examDays: calendar.examDays,
  retakeDays: calendar.retakeDays,
  outsideDays: [],
  holidays: calendar.holidays,
});

// The free, exam, retake and outside days of the teaching period by 'YYYY-MM-DD'
export const getMarkedDays = (
  calendar: Pick<
    CalendarDays,
    'freeDays' | 'examDays' | 'retakeDays' | 'outsideDays'
  >
): Map<string, DayStatus> =>
  new Map<string, DayStatus>([
    ...calendar.freeDays.map((key): [string, DayStatus] => [key, 'free']),
    ...calendar.examDays.map((key): [string, DayStatus] => [key, 'exam']),
    ...calendar.retakeDays.map((key): [string, DayStatus] => [key, 'retake']),
    ...calendar.outsideDays.map((key): [string, DayStatus] => [key, 'outside']),
  ]);

// Status of a day ('YYYY-MM-DD') in a calendar: outside of the teaching period,
// inside it free, exam, retake or outside if it is marked so, otherwise working
export const getStudyYearDayStatus = (
  calendar: Pick<CalendarDays, 'startDate' | 'endDate'>,
  markedDays: ReadonlyMap<string, DayStatus>,
  key: string
): DayStatus => {
  if (key < calendar.startDate || key > calendar.endDate) {
    return 'outside';
  }
  return markedDays.get(key) ?? 'working';
};
